import React, { useState, useEffect } from "react";
import useLevelBookingWarning from "hooks/useLevelBookingWarning";
import { Form, Select, Radio } from "antd";
import { useQuery, useMutation, gql } from "@apollo/client";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// graphql
import GET_LEVELS from "graphql/queries/GetLevels";
import GET_LEVEL_DETAILS from "graphql/queries/GetLevelDetails";
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import GET_NAVBAR_INFO from "graphql/queries/GetNavbarInfo";
import GET_MY_CLASSES from "graphql/queries/GetMyClasses";
import BOOK_ONE_ON_ONE_CLASS from "graphql/mutations/BookOneOnOneClass";
import GET_LANGUAGE_DETAILS from "graphql/queries/GetLanguageDetails";

// import types for antd components
import { SelectValue } from "antd/lib/select";
import { RadioChangeEvent } from "antd/lib/radio";

// custom types
import { Level, Chapter, Topic, City, OOOFormState } from "types";

// common
import { ModalLayout, MoreModalButtons, ModalText } from "common/modal";

// utils
import { getLevelId } from "utils/language";
// styling
import {
  CustomModalOneOnOne,
  FormInputContainer,
  ModalSelect,
  SelectCity,
  StudentNote,
  SelectTailorMade,
  CreditInfo,
  Contact,
} from "./OneOnOneModalStyle";

const { ModalRow, ModalCol } = ModalLayout;
const { ButtonContainer, ModalButtonYellow } = MoreModalButtons;
const { CreditWarning } = ModalText;

const { Option } = Select;

type OneOnOneModalProps = {
  visible: boolean;
  onCloseModal: () => void;
  credits: number;
  classCredit: number;
  state: {
    classId: number;
    defaultLevelId: number;
    filteredLanguageId?: number;
  };
  handleBookingError: (error: any) => void;
  handleBookingSuccess: () => void;
};

const OneOnOneModal = ({
  visible,
  onCloseModal,
  credits,
  classCredit,
  state,
  handleBookingError,
  handleBookingSuccess,
}: OneOnOneModalProps) => {
  const { t } = useTranslation("book_a_class");

  const [form] = Form.useForm();
  const { confirmLevel } = useLevelBookingWarning();
  const [submitting, setSubmitting] = useState(false);

  const [modalState, setModalState] = useState<OOOFormState>({
    tailorMade: true,
    level: { id: state.defaultLevelId },
    notes: "",
    city: { id: 0 },
    topic: { id: 0 },
    chapter: { id: 0 },
  });

  // LEVEL INFO QUERY MANAGEMENT
  const { data: levelList } = useQuery(GET_LEVELS);
  const { data: levelDetails } = useQuery(GET_LEVEL_DETAILS, {
    variables: {
      id: modalState.level.id,
    },
    skip: modalState.level.id === 0,
  });
  const { data: language } = useQuery(GET_LANGUAGE_DETAILS, {
    variables: {
      id: state.filteredLanguageId,
    },
  });

  const cities = language?.languageDetails.cities || [];

  // BOOKING MUTATION
  const [bookOneOnOne] = useMutation(BOOK_ONE_ON_ONE_CLASS, {
    refetchQueries: [
      {
        query: GET_MY_CLASSES,
      },
      {
        query: GET_CURRENT_USER_INFO,
      },
      {
        query: GET_NAVBAR_INFO,
      },
    ],
    update: (cache, mutationsResult) => {
      const { data } = mutationsResult;
      cache.writeFragment({
        // eslint-disable-next-line no-underscore-dangle
        id: `${data.bookOneOnOneClass.appointment.bookedClass.__typename}:${state.classId}`,
        fragment: gql`
          fragment OneOnOneLesson on Lesson {
            bookedAt
          }
        `,
        data: {
          bookedAt: data.bookOneOnOneClass.appointment.bookedAt,
        },
      });
    },
    onError: (error) => handleBookingError(error.graphQLErrors),
    onCompleted: () => {
      handleBookingSuccess();
    },
  });

  useEffect(() => {
    if (state.filteredLanguageId && language?.languageDetails) {
      const levelId = getLevelId(
        language.languageDetails.id,
        [language.languageDetails],
        state.defaultLevelId
      );

      form.setFieldsValue({
        level: levelId,
        chapter: undefined,
        topic: undefined,
      });

      setModalState((prevState: any) => {
        return {
          ...prevState,
          level: { id: levelId },
        };
      });
    }
  }, [state.filteredLanguageId, language, state.defaultLevelId, form]);

  const onConfirmBooking = async () => {
    const { tailorMade, level, chapter, topic, city, notes } = modalState;
    setSubmitting(true);
    try {
      if (await confirmLevel({ levelId: level.id })) {
        await bookOneOnOne({
          variables: {
            tailorMade,
            lessonId: state.classId,
            levelId: level.id,
            notes,
            chapterId: chapter.id,
            topicId: topic.id,
            cityId: city.id,
          },
        });
        onCloseModal();
      }
    } catch (error) {
      handleBookingError([
        { message: t("Unable to complete your booking. Please try again.") },
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  // COMPONENT RENDERING
  const renderTopicList = () => {
    const chosenChapter = levelDetails?.levelDetails.chapters.find(
      (chapter: Chapter) => {
        return chapter.id === modalState.chapter.id;
      }
    );
    return chosenChapter?.topics.map((topic: Topic) => (
      <Option value={topic.id} key={topic.id}>
        {topic.title}
      </Option>
    ));
  };

  const renderLevelSelector = () => {
    const width = modalState.tailorMade ? 24 : 12;
    return (
      <ModalCol xs={24} sm={24} md={width} lg={width} xl={width}>
        <Form.Item
          name="level"
          rules={[
            {
              required: true,
              message: t("Level is required."),
            },
          ]}
          initialValue={modalState.level.id}
        >
          <ModalSelect
            showSearch
            placeholder={t("My Level")}
            optionFilterProp="children"
            onChange={(value: SelectValue) => {
              setModalState({
                ...modalState,
                level: { id: value as number },
              });
              form.setFieldsValue({
                chapter: undefined,
                topic: undefined,
              });
            }}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {state?.filteredLanguageId
              ? language?.languageDetails.levels.map((level: Level) => (
                  <Option value={level.id} key={level.title}>
                    {level.title}
                  </Option>
                ))
              : levelList?.levels.map((level: Level) => (
                  <Option value={level.id} key={level.title}>
                    {level.title}
                  </Option>
                ))}
          </ModalSelect>
        </Form.Item>
      </ModalCol>
    );
  };

  const renderChapterSelector = () => {
    if (!modalState.tailorMade) {
      return (
        <ModalCol xs={24} sm={24} md={12} lg={12} xl={12}>
          <Form.Item
            name="chapter"
            rules={[
              {
                required: true,
                message: t("Chapter is required."),
              },
            ]}
          >
            <ModalSelect
              showSearch
              placeholder={t("Choose a Chapter")}
              optionFilterProp="children"
              onChange={(value: SelectValue) => {
                setModalState({
                  ...modalState,
                  chapter: { id: value as number },
                });
                form.setFieldsValue({ topic: undefined });
              }}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {levelDetails?.levelDetails.chapters.map((chapter: Chapter) => (
                <Option value={chapter.id} key={chapter.name}>
                  {chapter.name}
                </Option>
              ))}
            </ModalSelect>
          </Form.Item>
        </ModalCol>
      );
    }
    return null;
  };

  const renderTopicSelector = () => {
    return (
      <ModalCol xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item
          name="topic"
          rules={[
            {
              required: true,
              message: t("Topic is required."),
            },
          ]}
        >
          <ModalSelect
            disabled={modalState.tailorMade}
            showSearch
            placeholder={t("Choose a Topic")}
            optionFilterProp="children"
            onChange={(value: SelectValue) => {
              setModalState({
                ...modalState,
                topic: { id: value as number },
              });
            }}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {renderTopicList()}
          </ModalSelect>
        </Form.Item>
      </ModalCol>
    );
  };

  const renderCitySelector = () => {
    return (
      <ModalCol xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item
          name="city"
          rules={[
            {
              required: true,
              message: t("City version is required."),
            },
          ]}
        >
          <SelectCity>
            {cities.map((city: City) => (
              <Radio
                key={city.id}
                onChange={(ev: RadioChangeEvent) => {
                  setModalState({
                    ...modalState,
                    city: { id: ev.target.value as number },
                  });
                }}
                value={city.id}
              >
                {city.name}
              </Radio>
            ))}
          </SelectCity>
        </Form.Item>
      </ModalCol>
    );
  };

  const renderTopicAndCitySelectors = () => {
    if (!modalState.tailorMade) {
      return (
        <ModalRow gutter={20}>
          {renderTopicSelector()}
          {renderCitySelector()}
        </ModalRow>
      );
    }
    return null;
  };

  const renderNotes = () => {
    const placeholderText = modalState.tailorMade
      ? t("I want to learn...")
      : t("Notes to teacher...");
    return (
      <Form.Item name="note">
        <StudentNote
          autoSize={{ minRows: 3, maxRows: 3 }}
          placeholder={placeholderText}
          onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) => {
            setModalState({
              ...modalState,
              notes: ev.target.value as string,
            });
          }}
        />
      </Form.Item>
    );
  };

  return (
    <CustomModalOneOnOne
      title={t("MY 1-ON-1 CLASS")}
      centered
      visible={visible}
      footer={null}
      onCancel={onCloseModal}
      forceRender
    >
      <Form onFinish={onConfirmBooking} form={form}>
        <FormInputContainer>
          <ModalCol xs={24} sm={24} md={15} lg={15} xl={15}>
            <Form.Item name="tailorMade" initialValue={modalState.tailorMade}>
              <SelectTailorMade
                onChange={(ev: RadioChangeEvent) => {
                  setModalState({
                    ...modalState,
                    tailorMade: ev.target.value as boolean,
                  });
                }}
              >
                <Radio.Button value>{t("Tailor Made")}</Radio.Button>
                <Radio.Button value={false}>{t("Study a Topic")}</Radio.Button>
              </SelectTailorMade>
            </Form.Item>
          </ModalCol>

          <ModalRow gutter={20} id="levelAndChapter">
            {renderLevelSelector()}
            {renderChapterSelector()}
          </ModalRow>

          {renderTopicAndCitySelectors()}
          {renderNotes()}
        </FormInputContainer>

        <ModalRow gutter={20}>
          <ModalCol xs={24} sm={24} md={12} lg={12} xl={12}>
            <CreditInfo>
              <span>
                {t("This 1 on 1 class costs {{ classCredit }} credit", {
                  classCredit,
                })}
              </span>
              <span>
                {t("You currently have {{ credits }} credits left.", {
                  credits,
                })}
              </span>
              {credits < classCredit ? (
                <CreditWarning>
                  {t("You have insufficient credits.")}{" "}
                  <Link
                    to="/my-credits"
                    style={{ textDecoration: "underline" }}
                  >
                    {t("Buy now")} &gt;
                  </Link>
                </CreditWarning>
              ) : null}
              <span>
                {t(
                  "You can cancel all classes at any time, however credits will only be refunded if you do so at least {{ hour }} hours beforehand.",
                  { hour: 24 }
                )}
              </span>
            </CreditInfo>
          </ModalCol>

          <ButtonContainer xs={20} sm={20} md={12}>
            <Form.Item>
              <ModalButtonYellow
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={credits < classCredit}
              >
                {t("BOOK CLASS")}
              </ModalButtonYellow>
            </Form.Item>
          </ButtonContainer>
        </ModalRow>
      </Form>

      <Contact>
        {t(
          "All LTL teachers are highly qualified and professional instructors. As each person has their own schedule it is not possible to request a specific teacher. To study with a specific teacher and a fixed schedule please email"
        )}{" "}
        <a href="mailto:support@flexiclasses.com">support@flexiclasses.com</a>.
      </Contact>
    </CustomModalOneOnOne>
  );
};

export default OneOnOneModal;
